/**
 * components/import/statements/RowAccountPicker.jsx — Phase 84.3 slice-1
 *
 * Per-file account selector with a sticky "+ Create new account" item at
 * the top of the dropdown. Used by every queue row in the bulk-backfill
 * panel. Extracted verbatim from pages/CSVImport.js (was internal function
 * `RowAccountPicker`). Zero behavior change.
 *
 * Variants:
 *   • default  : solid primary button, used when the row needs an account
 *   • outline  : outlined secondary, used as "Pick existing" alongside the
 *                primary "+ Create account" CTA in 'detected_new' state
 *   • ghost    : minimal "Change", used to override an already-matched row
 *
 * Behaviour:
 *   • Selecting "__create__" calls onCreateClick() instead of onChange(),
 *     letting the parent open NewAccountInlineDialog with prefill defaults.
 *   • Selecting an account calls onChange(accountId).
 */
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus } from 'lucide-react';

export default function RowAccountPicker({ value, accounts, onChange, onCreateClick, triggerLabel = 'Account…', variant = 'outline', testid }) {
  const selected = (accounts || []).find((a) => a.id === value);
  const variantCls = variant === 'default'
    ? 'bg-primary text-primary-foreground hover:bg-primary/90 border-primary'
    : variant === 'ghost'
      ? 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent/60 border-transparent'
      : 'bg-card hover:bg-accent/60 border-border/70';
  // Ghost variant (used to override an already-matched row) always shows
  // the trigger label so it doesn't visually duplicate the account name
  // that the row already displays prominently with the green check.
  const showSelectedInTrigger = variant !== 'ghost';
  return (
    <Select
      value={value || undefined}
      onValueChange={(v) => {
        if (v === '__create__') {
          onCreateClick?.();
          return;
        }
        onChange?.(v);
      }}
    >
      <SelectTrigger
        className={`h-7 rounded-lg text-[11.5px] px-2.5 border ${variantCls} focus:ring-2 focus:ring-ring/40 [&>svg]:opacity-70 w-auto min-w-[110px] max-w-[200px] gap-1.5`}
        data-testid={testid}
      >
        <SelectValue placeholder={triggerLabel}>
          {selected && showSelectedInTrigger ? (
            <span className="inline-flex items-center gap-1.5 truncate">
              <span className="truncate">{selected.name}</span>
              {selected.last4 && <span className="text-muted-foreground tabular-nums text-[10.5px]">&bull;&bull;{selected.last4}</span>}
            </span>
          ) : (
            triggerLabel
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__create__" className="font-medium text-primary focus:text-primary">
          <span className="inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Create new account
          </span>
        </SelectItem>
        {(accounts || []).length > 0 && (
          <div className="my-1 h-px bg-border/60" role="separator" />
        )}
        {(accounts || []).map((a) => (
          <SelectItem key={a.id} value={a.id}>
            <span className="inline-flex items-center gap-1.5 truncate">
              <span className="truncate">{a.name}</span>
              {a.last4 && <span className="text-muted-foreground tabular-nums text-[10.5px]">&bull;&bull;{a.last4}</span>}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
